import { useEffect, useRef } from 'react'
import './SearchInput.css'

/**
 * 검색 입력 컴포넌트 — 모든 네비게이션 검색에서 공통 사용.
 *
 * Props:
 *   value         — 현재 검색어 (controlled)
 *   onChange(v)   — 변경 콜백
 *   placeholder   — 입력 placeholder (default '검색 (Ctrl+K)')
 *   total         — 전체 항목 수 (배지 표시용)
 *   matched       — 매치된 항목 수. value 비면 안 보임
 *   hotkey        — 단축키 ('k' 기본). Ctrl/Cmd + hotkey 누르면 input 포커스
 *   children      — 우측 추가 토글/버튼 슬롯 (예: 내 것만 토글)
 *   compact       — 컴팩트 모드 (좁은 사이드바용)
 *
 * 단축키: 컴포넌트 마운트 동안 window keydown 리스너 — input 이 다른 곳에 포커스
 * 중이거나 modifier 키 조합이 아니면 무시. 검색 input 포커스 + 텍스트 전체 선택.
 */
export default function SearchInput({
  value,
  onChange,
  placeholder = '검색 (Ctrl+K)',
  total,
  matched,
  hotkey = 'k',
  children,
  compact = false,
}) {
  const ref = useRef(null)

  // Ctrl/Cmd + hotkey → 검색 input 포커스 (브라우저 기본 동작 override)
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      if (e.key.toLowerCase() !== hotkey.toLowerCase()) return
      // 다른 모달/dialog 에 입력 중이면 패스 (필요 시 정교화)
      e.preventDefault()
      const el = ref.current
      if (el) {
        el.focus()
        el.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [hotkey])

  const showCount = !!value
  const handleClear = () => {
    onChange('')
    ref.current?.focus()
  }

  return (
    <div className={`search-input ${compact ? 'is-compact' : ''}`}>
      <span className="search-input-icon" aria-hidden>🔍</span>
      <input
        ref={ref}
        type="text"
        className="search-input-field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label="검색"
      />
      {value && (
        <button
          type="button"
          className="search-input-clear"
          onClick={handleClear}
          title="검색어 지우기 (Esc)"
          onKeyDown={(e) => { if (e.key === 'Escape') handleClear() }}
          aria-label="검색어 지우기"
        >✕</button>
      )}
      {showCount && (
        <span className="search-input-count" title={`${matched ?? '?'} / 전체 ${total ?? '?'}`}>
          {matched ?? 0}/{total ?? 0}
        </span>
      )}
      {children && <span className="search-input-extras">{children}</span>}
    </div>
  )
}
